import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  addDoc,
  updateDoc,
  query,
  where,
  serverTimestamp
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import {
  IPS_ROOT_DOC,
  IPS_ADMIN_PASSWORD,
  IPS_DAYS,
  DEFAULT_PLAN_ROWS,
  CONTENT_TYPES
} from './ipsConstants';
import {
  formatDateInput,
  getDayKeyFromDate,
  getWeekStartMonday
} from './ipsDateUtils';

export {
  IPS_ROOT_DOC,
  IPS_ADMIN_PASSWORD,
  IPS_DAYS,
  DEFAULT_PLAN_ROWS,
  CONTENT_TYPES
};

export {
  formatDateInput,
  getDayKeyFromDate,
  getDayLabelFromDate,
  getWeekStartMonday,
  formatDateLabel,
  formatDateFullLabelLines,
  formatDateFullLabel
} from './ipsDateUtils';

export {
  downloadIpsFile,
  buildIpsDownloadFileName,
  getIpsContentFiles,
  isCarouselContent,
  isVideoMediaFile,
  isImageMediaFile,
  openIpsMediaInNewTab,
  buildIpsMediaDownloadUrl,
  buildIpsVideoDownloadUrl,
  triggerIpsMediaDownload,
  downloadAllIpsFiles,
  slugifyIpsLabel
} from './ipsDownload';

export const createDefaultPlanRows = () =>
  DEFAULT_PLAN_ROWS.map((label, index) => ({
    id: `row-${index + 1}`,
    label
  }));

const planTemplateRef = () => doc(db, ...IPS_ROOT_DOC, 'planTemplate', 'current');
const dayNotesRef = (dateStr) => doc(db, ...IPS_ROOT_DOC, 'dayNotes', dateStr);

const contentsCol = () => collection(db, ...IPS_ROOT_DOC, 'contents');

export async function ensureIpsRootDoc() {
  await setDoc(
    doc(db, ...IPS_ROOT_DOC),
    { initialized: true, updatedAt: serverTimestamp() },
    { merge: true }
  );
}

export async function getWeeklyPlan(planDate) {
  if (!planDate) return null;
  const snap = await getDoc(doc(db, ...IPS_ROOT_DOC, 'weeklyPlans', planDate));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

export async function saveWeeklyPlan(planDate, rows) {
  if (!planDate) throw new Error('Plan tarihi gerekli.');
  await ensureIpsRootDoc();
  const payload = {
    planDate,
    rows: Array.isArray(rows) ? rows : createDefaultPlanRows(),
    updatedAt: serverTimestamp()
  };
  await setDoc(doc(db, ...IPS_ROOT_DOC, 'weeklyPlans', planDate), payload, { merge: true });
  return payload;
}

export async function getContentsByDate(dateStr) {
  if (!dateStr) return [];
  const q = query(contentsCol(), where('date', '==', dateStr));
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) =>
      parseTimeForSort(a.publishTime).localeCompare(parseTimeForSort(b.publishTime))
    );
}

export async function saveContent(content, files) {
  const fileList = normalizeUploadFiles(files);
  if (!fileList.length) throw new Error('Dosya gerekli.');

  await ensureIpsRootDoc();
  const docRef = await addDoc(contentsCol(), {
    date: content.date,
    type: content.type,
    planRowId: content.planRowId || '',
    planRowLabel: content.planRowLabel || '',
    title: content.title || '',
    description: content.description || '',
    tags: content.tags || '',
    link: content.link || '',
    publishTime: content.publishTime || '',
    mediaUrl: '',
    mediaType: '',
    fileName: '',
    mediaFiles: [],
    shared: false,
    sharedAt: null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });

  const mediaFiles = [];

  for (let i = 0; i < fileList.length; i += 1) {
    const file = fileList[i];
    const storageFileName = `${String(i + 1).padStart(2, '0')}${extensionFromFile(file)}`;
    const storagePath = `ukba_kasa/instagram_ips/${content.date}/${docRef.id}/${storageFileName}`;
    const storageRef = ref(storage, storagePath);
    await uploadBytes(storageRef, file);
    const url = await getDownloadURL(storageRef);
    const mediaType = file.type.startsWith('video/') ? 'video' : 'image';
    mediaFiles.push({
      order: i + 1,
      url,
      mediaType,
      fileName: storageFileName
    });
  }

  const first = mediaFiles[0];
  const updatePayload = {
    mediaUrl: first?.url || '',
    mediaType: first?.mediaType || '',
    fileName: first?.fileName || '',
    updatedAt: serverTimestamp()
  };

  if (isGonderiContentType(content.type)) {
    updatePayload.mediaFiles = mediaFiles;
  } else if (mediaFiles.length > 1) {
    updatePayload.mediaFiles = mediaFiles;
  }

  await updateDoc(docRef, updatePayload);

  return {
    id: docRef.id,
    ...content,
    mediaUrl: updatePayload.mediaUrl,
    mediaType: updatePayload.mediaType,
    fileName: updatePayload.fileName,
    mediaFiles: updatePayload.mediaFiles || [],
    shared: false
  };
}

export async function markContentShared(contentId) {
  const docRef = doc(db, ...IPS_ROOT_DOC, 'contents', contentId);
  await updateDoc(docRef, {
    shared: true,
    sharedAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
}

export function parseTimeForSort(timeStr) {
  if (!timeStr) return '99:99';
  return timeStr;
}

const PLAN_TEMPLATE_CACHE_KEY = 'ips_plan_template_v1';
const DAY_NOTES_CACHE_PREFIX = 'ips_day_notes_';

function normalizePlanRows(rows) {
  if (!Array.isArray(rows)) return createDefaultPlanRows();
  return rows
    .filter((row) => row?.id && row?.label != null)
    .map((row) => ({ id: row.id, label: String(row.label) }));
}

function loadPlanTemplateFromCache() {
  try {
    const raw = localStorage.getItem(PLAN_TEMPLATE_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const rows = normalizePlanRows(parsed?.rows);
    return rows.length ? { rows } : null;
  } catch {
    return null;
  }
}

function savePlanTemplateToCache(rows) {
  try {
    localStorage.setItem(PLAN_TEMPLATE_CACHE_KEY, JSON.stringify({ rows: normalizePlanRows(rows) }));
  } catch {
    // ignore quota errors
  }
}

function loadDayNotesFromCache(dateStr) {
  try {
    const raw = localStorage.getItem(`${DAY_NOTES_CACHE_PREFIX}${dateStr}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.notes && typeof parsed.notes === 'object' ? parsed.notes : null;
  } catch {
    return null;
  }
}

function saveDayNotesToCache(dateStr, notes) {
  try {
    localStorage.setItem(
      `${DAY_NOTES_CACHE_PREFIX}${dateStr}`,
      JSON.stringify({ date: dateStr, notes: notes || {} })
    );
  } catch {
    // ignore quota errors
  }
}

async function migratePlanTemplateFromWeeklyPlan() {
  const weekId = getWeekStartMonday(formatDateInput());
  const weekly = await getWeeklyPlan(weekId);
  if (!weekly?.rows?.length) return null;
  const rows = normalizePlanRows(weekly.rows);
  if (!rows.length) return null;
  await savePlanTemplate(rows);
  return { rows };
}

async function migrateDayNotesFromWeeklyPlan(dateStr) {
  const weekId = getWeekStartMonday(dateStr);
  const weekly = await getWeeklyPlan(weekId);
  if (!weekly?.rows?.length) return {};
  const dayKey = getDayKeyFromDate(dateStr);
  const notes = {};
  weekly.rows.forEach((row) => {
    const note = row.cells?.[dayKey];
    if (note) notes[row.id] = note;
  });
  return notes;
}

export async function getPlanTemplate() {
  const snap = await getDoc(planTemplateRef());
  if (!snap.exists()) return null;
  const rows = normalizePlanRows(snap.data()?.rows);
  return rows.length ? { rows } : null;
}

export async function savePlanTemplate(rows) {
  const normalized = normalizePlanRows(rows);
  await ensureIpsRootDoc();
  await setDoc(
    planTemplateRef(),
    { rows: normalized, updatedAt: serverTimestamp() },
    { merge: true }
  );
  return { rows: normalized };
}

export async function getPlanTemplateSafe() {
  try {
    const remote = await getPlanTemplate();
    if (remote?.rows?.length) {
      savePlanTemplateToCache(remote.rows);
      return remote;
    }
  } catch (err) {
    console.warn('IPS plan template remote load failed:', err);
  }

  try {
    const migrated = await migratePlanTemplateFromWeeklyPlan();
    if (migrated?.rows?.length) {
      savePlanTemplateToCache(migrated.rows);
      return migrated;
    }
  } catch (err) {
    console.warn('IPS plan template migration failed:', err);
  }

  const cached = loadPlanTemplateFromCache();
  if (cached?.rows?.length) return cached;
  return null;
}

export async function savePlanTemplateSafe(rows) {
  const normalized = normalizePlanRows(rows);
  savePlanTemplateToCache(normalized);
  try {
    await savePlanTemplate(normalized);
  } catch (err) {
    console.warn('IPS plan template remote save failed:', err);
    throw err;
  }
}

export async function getDayNotesByDate(dateStr) {
  if (!dateStr) return {};
  const snap = await getDoc(dayNotesRef(dateStr));
  if (snap.exists()) {
    const notes = snap.data()?.notes;
    return notes && typeof notes === 'object' ? notes : {};
  }
  return migrateDayNotesFromWeeklyPlan(dateStr);
}

export async function getDayNotesSafe(dateStr) {
  if (!dateStr) return {};
  const cached = loadDayNotesFromCache(dateStr);
  try {
    const remote = await getDayNotesByDate(dateStr);
    if (remote && Object.keys(remote).length) {
      saveDayNotesToCache(dateStr, remote);
    }
    return remote || {};
  } catch (err) {
    console.warn('IPS day notes load failed:', err);
    return cached || {};
  }
}

export async function saveDayNotes(dateStr, notes) {
  if (!dateStr) throw new Error('Tarih gerekli.');
  const payload = {
    date: dateStr,
    notes: notes && typeof notes === 'object' ? notes : {},
    updatedAt: serverTimestamp()
  };
  await ensureIpsRootDoc();
  await setDoc(dayNotesRef(dateStr), payload, { merge: true });
  return payload;
}

export async function saveDayNotesSafe(dateStr, notes) {
  saveDayNotesToCache(dateStr, notes);
  try {
    await saveDayNotes(dateStr, notes);
  } catch (err) {
    console.warn('IPS day notes remote save failed:', err);
    throw err;
  }
}

/** @deprecated Haftalık plan — yalnızca eski veriden göç için */
export async function getWeeklyPlanSafe(planDate) {
  try {
    const remote = await getWeeklyPlan(planDate);
    if (remote?.rows?.length) return remote;
  } catch (err) {
    console.warn('IPS weekly plan load failed:', err);
  }
  return null;
}

export function inferContentTypeFromRowLabel(label = '') {
  const l = String(label).toLowerCase();
  if (l.includes('whatsapp') && l.includes('kanal')) return 'WhatsApp Kanalı';
  if (l.includes('whatsapp')) return 'WhatsApp Hikaye';
  if (l.includes('reels')) return 'Reels';
  if (l.includes('gönderi') || l.includes('gonderi')) return 'Gönderi';
  if (l.includes('hikaye')) return 'Hikaye';
  return null;
}

export function isGonderiContentType(typeOrLabel = '') {
  const value = String(typeOrLabel).toLowerCase();
  return value === 'gönderi' || value === 'gonderi' || value.includes('gönderi') || value.includes('gonderi');
}

function normalizeUploadFiles(files) {
  if (!files) return [];
  return (Array.isArray(files) ? files : [files]).filter(Boolean);
}

function extensionFromFile(file) {
  const name = String(file?.name || '');
  const fromName = name.match(/(\.[a-z0-9]{2,5})$/i);
  if (fromName) return fromName[1].toLowerCase();
  if (file.type === 'image/png') return '.png';
  if (file.type === 'image/webp') return '.webp';
  if (file.type === 'image/gif') return '.gif';
  if (file.type.startsWith('video/')) return '.mp4';
  return '.jpg';
}

export function contentHasUploadedMedia(content) {
  if (!content) return false;
  if (content.mediaUrl) return true;
  return Array.isArray(content.mediaFiles) && content.mediaFiles.some((entry) => entry?.url);
}

/** Plan satırlarını seçilen günün yüklenmiş içerikleriyle eşleştirir. */
export function mapPlanRowsToContents(planRows, dayNotes = {}, contents, manualShares = []) {
  const rows = Array.isArray(planRows) ? planRows : [];
  const notes = dayNotes && typeof dayNotes === 'object' ? dayNotes : {};
  const list = Array.isArray(contents) ? contents : [];
  const manualByRowId = {};
  manualShares.forEach((entry) => {
    if (entry?.planRowId) manualByRowId[entry.planRowId] = entry;
  });

  const contentByPlanRowId = {};
  const unassigned = [];
  list.forEach((item) => {
    if (item.planRowId) {
      if (!contentByPlanRowId[item.planRowId]) {
        contentByPlanRowId[item.planRowId] = item;
      } else {
        unassigned.push(item);
      }
    } else {
      unassigned.push(item);
    }
  });

  const usedIds = new Set(Object.values(contentByPlanRowId).map((item) => item.id));
  const byType = {};
  unassigned.forEach((item) => {
    const type = item.type;
    if (!byType[type]) byType[type] = [];
    byType[type].push(item);
  });

  const typeCounters = {};

  return rows.map((row) => {
    const note = notes[row.id] || '';
    let content = contentByPlanRowId[row.id] || null;

    if (!content) {
      const contentType = inferContentTypeFromRowLabel(row.label);
      if (contentType && byType[contentType]) {
        const idx = typeCounters[contentType] || 0;
        typeCounters[contentType] = idx + 1;
        content = byType[contentType].find((c) => !usedIds.has(c.id)) || null;
        if (content) usedIds.add(content.id);
      }
    }

    const manual = manualByRowId[row.id];
    const shared = !!(content?.shared || manual?.shared);

    return {
      id: row.id,
      label: row.label,
      note,
      content,
      uploaded: contentHasUploadedMedia(content),
      shared,
      manualShared: !!(manual?.shared && !content?.shared)
    };
  });
}

/** Seçilen gün için henüz yüklenmemiş plan satırları. */
export function getUploadablePlanRows(planRows, contents) {
  return mapPlanRowsToContents(planRows, {}, contents)
    .filter((item) => !item.uploaded)
    .map((item) => ({ id: item.id, label: item.label }));
}

export function buildShareReport(planRows, dayNotes, contents, manualShares = []) {
  const items = mapPlanRowsToContents(planRows, dayNotes, contents, manualShares);
  const total = items.length;
  const uploaded = items.filter((item) => item.uploaded).length;
  const shared = items.filter((item) => item.shared).length;
  const sharedPctOfTotal = total ? Math.round((shared / total) * 100) : 0;
  const sharedPctOfUploaded = uploaded ? Math.round((shared / uploaded) * 100) : 0;

  return {
    items,
    total,
    uploaded,
    shared,
    sharedPctOfTotal,
    sharedPctOfUploaded
  };
}

export function manualShareDocId(date, planRowId) {
  return `${date}_${planRowId}`;
}

const MANUAL_SHARE_CACHE_PREFIX = 'ips_manual_shares_';

export function loadManualSharesFromCache(dateStr) {
  try {
    const raw = localStorage.getItem(`${MANUAL_SHARE_CACHE_PREFIX}${dateStr}`);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed?.entries) ? parsed.entries : [];
  } catch {
    return [];
  }
}

export function saveManualSharesToCache(dateStr, entries) {
  try {
    localStorage.setItem(
      `${MANUAL_SHARE_CACHE_PREFIX}${dateStr}`,
      JSON.stringify({ date: dateStr, entries })
    );
  } catch {
    // ignore quota errors
  }
}

function mergeManualShares(remote, cached) {
  const byRowId = {};
  cached.forEach((entry) => {
    if (entry?.planRowId) byRowId[entry.planRowId] = entry;
  });
  remote.forEach((entry) => {
    if (entry?.planRowId) byRowId[entry.planRowId] = entry;
  });
  return Object.values(byRowId);
}

export async function getManualSharesByDate(dateStr) {
  if (!dateStr) return [];
  const cached = loadManualSharesFromCache(dateStr);
  try {
    const q = query(
      collection(db, ...IPS_ROOT_DOC, 'manualShares'),
      where('date', '==', dateStr)
    );
    const snap = await getDocs(q);
    const remote = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    if (remote.length) {
      saveManualSharesToCache(dateStr, remote);
    }
    return mergeManualShares(remote, cached);
  } catch (err) {
    console.warn('IPS manual shares load failed:', err);
    return cached;
  }
}

export async function markPlanRowSharedManually(date, planRowId, planRowLabel) {
  if (!date || !planRowId) throw new Error('Tarih ve plan satırı gerekli.');

  const docId = manualShareDocId(date, planRowId);
  const entry = {
    id: docId,
    date,
    planRowId,
    planRowLabel: planRowLabel || '',
    shared: true,
    sharedAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };

  const cached = loadManualSharesFromCache(date).filter((item) => item.planRowId !== planRowId);
  saveManualSharesToCache(date, [...cached, entry]);

  try {
    await ensureIpsRootDoc();
    await setDoc(
      doc(db, ...IPS_ROOT_DOC, 'manualShares', docId),
      {
        date,
        planRowId,
        planRowLabel: planRowLabel || '',
        shared: true,
        sharedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      },
      { merge: true }
    );
  } catch (err) {
    console.warn('IPS manual share remote save failed:', err);
  }
}
